import { getRouteApi } from "@tanstack/react-router"
import { z } from "zod"

export const VIEWS = ["timeline", "board"] as const

export type View = (typeof VIEWS)[number]

export const RANGES = ["two-weeks", "month", "quarter"] as const

export type Range = (typeof RANGES)[number]

export const GROUPINGS = ["department", "week", "phase"] as const

export type Grouping = (typeof GROUPINGS)[number]

export const GROUPING_LABELS: Record<Grouping, string> = {
  department: "Department",
  week: "Week",
  phase: "Phase",
}

export const planSearchSchema = z.object({
  view: z.enum(VIEWS).optional().catch(undefined),
  range: z.enum(RANGES).optional().catch(undefined),
  start: z.number().int().optional().catch(undefined),
  group: z
    .enum([...GROUPINGS, "none"])
    .optional()
    .catch(undefined),
  subgroup: z.enum(GROUPINGS).optional().catch(undefined),
  shops: z.boolean().optional().catch(undefined),
  cascade: z.boolean().optional().catch(undefined),
  focus: z.string().optional().catch(undefined),
})

export type PlanSearch = z.infer<typeof planSearchSchema>

export type PlanDisplay = {
  view: View
  range: Range
  group: Grouping | undefined
  subgroup: Grouping | undefined
  shops: boolean
  cascade: boolean
  focus: string | undefined
}

export function displayOf(search: PlanSearch): PlanDisplay {
  const group =
    search.group === "none" ? undefined : (search.group ?? "department")
  return {
    view: search.view ?? "timeline",
    range: search.range ?? "month",
    group,
    subgroup: group && search.subgroup !== group ? search.subgroup : undefined,
    shops: search.shops ?? true,
    cascade: search.cascade ?? true,
    focus: search.focus,
  }
}

export function viewLabel(view: View): string {
  switch (view) {
    case "timeline":
      return "Timeline"
    case "board":
      return "Board"
  }
}

const RANGE_DAYS: Record<Range, number> = {
  "two-weeks": 14,
  month: 30,
  quarter: 91,
}

const MIN_DAY_WIDTH: Record<Range, number> = {
  "two-weeks": 60,
  month: 36,
  quarter: 14,
}

export function rangeDays(range: Range): number {
  return RANGE_DAYS[range]
}

export function rangeStep(range: Range): number {
  return range === "month" ? 28 : RANGE_DAYS[range]
}

export function dayWidthFor(range: Range, width: number | undefined): number {
  return Math.max(
    MIN_DAY_WIDTH[range],
    Math.floor((width ?? 0) / RANGE_DAYS[range]),
  )
}

export function rangeLabel(range: Range): string {
  switch (range) {
    case "two-weeks":
      return "2 weeks"
    case "month":
      return "Month"
    case "quarter":
      return "Quarter"
  }
}

const planRoute = getRouteApi("/_layout/planning_/$planId")

export function usePlanParams(): { planId: string } {
  return planRoute.useParams()
}

export function usePlanSearch(): PlanSearch {
  return planRoute.useSearch()
}

export function useUpdatePlanSearch(): (patch: Partial<PlanSearch>) => void {
  const navigate = planRoute.useNavigate()
  return (patch) =>
    navigate({
      search: (previous) => ({ ...previous, ...patch }),
      replace: true,
    })
}
